/**
 * Feature E05-F07 — "Admin: AI-провайдеры" request/response contracts.
 *
 * NEW in v2 (no legacy n8n equivalent). Lets an admin choose the ACTIVE AI
 * provider + model + API key, plus a SECONDARY (fallback) provider/model/key,
 * stored DB-side (table `ai_provider_settings`, two rows keyed by role
 * primary|secondary) — replacing the env-only ANTHROPIC_API_KEY /
 * OPENAI_API_KEY / LLM_PRIMARY config. The env values remain a FALLBACK: an empty
 * table keeps the system working exactly as before (resolver: DB → env).
 *
 * Endpoints honoured by the feature route (`routes/admin-ai-providers.ts`,
 * mounted under `/admin`, ALL admin-only — `app.requireRole('admin')`):
 *   GET /admin/ai-providers → { success?, primary: AiProviderSlot, secondary }
 *   PUT /admin/ai-providers → { success?, primary: AiProviderSlot, secondary }
 *                             (body: AiProviderSettingsUpdate — full replace)
 *
 * SECURITY invariants (carried by these shapes — do NOT relax):
 *   - The API NEVER returns the plaintext key. On READ each slot carries only a
 *     MASKED tail (`maskedKey` e.g. '····1234') + `hasKey:true`; the ciphertext
 *     and the key itself never cross the wire.
 *   - On WRITE `apiKey` is OPTIONAL per slot: present ⇒ (re-)encrypt & store;
 *     ABSENT/undefined ⇒ LEAVE the stored key UNCHANGED (so an admin can edit the
 *     model without re-typing the key, and the masked read can be re-submitted
 *     without leaking). An explicit empty string `''` means CLEAR the key (revert
 *     that slot to env fallback) — see `apiKeyAction` discrimination below.
 *
 * MODEL semantics: The effective model per task now lives on `ai_prompts.primary_model`
 * and `ai_prompts.fallback_model` (see `docs/superpowers/specs/2026-08-30-model-per-task-matrix-design.md`).
 * Provider slots no longer carry a model override.
 *
 * `success: z.literal(true).optional()` on every object response — the web
 * apiClient STRIPS a top-level `success`; making it required would throw ZodError
 * (project-wide success-strip convention; see admin-prompts / admin-teams).
 *
 * Lives in its OWN file; the barrel re-export (`export *`) is added to
 * packages/shared/src/index.ts by the same change.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Provider registry (extensibility seam — add a 3rd provider here, e.g. gemini)
// ---------------------------------------------------------------------------
/**
 * The providers with a working provider class TODAY
 * (services/llm-provider-anthropic.ts, llm-provider-openai.ts). A 3rd provider
 * (Gemini) is intentionally NOT here yet — adding it is: a value in this enum +
 * a model list below + a provider class. No migration is needed (the DB column is
 * a plain VarChar).
 */
export const AI_PROVIDERS = ['anthropic', 'openai'];
export const aiProviderSchema = z.enum(AI_PROVIDERS);
/**
 * CURRENT model lists per provider (the dropdown options). Plus a CUSTOM_MODEL
 * affordance: the UI shows these + a "Custom…" free-text input; a custom id is
 * submitted verbatim (the contract keeps `model` a free string for that reason).
 *
 * Anthropic's list is INTENTIONALLY EMPTY: there is no static Anthropic model id
 * this codebase can vouch for as a reliable fallback, so the dropdown relies
 * entirely on `GET /admin/ai-providers/models` ("Get actual models"); until an
 * admin has fetched at least once (or the in-process cache — 6h TTL, lost on
 * restart — has expired), the primary/fallback `ModelSelect` for an Anthropic
 * slot shows the row's currently saved model as a "(stale)"-labeled option
 * instead of a curated list. OpenAI's list was not flagged as fictional and is
 * left as a sensible current set. `model` on the wire is NOT validated against
 * either list (custom escape) — they are a UI convenience + a shared
 * single-source-of-truth for the dropdown.
 */
export const ANTHROPIC_MODELS = [];
export const OPENAI_MODELS = ['gpt-4o', 'gpt-4o-mini', 'gpt-4.1', 'o3', 'o3-mini'];
/** The "Custom…" sentinel the dropdown uses to reveal the free-text model input. */
export const CUSTOM_MODEL_OPTION = 'custom';
/** Model options per provider, for the UI dropdown (does NOT include 'custom'). */
export const AI_PROVIDER_MODELS = {
    anthropic: ANTHROPIC_MODELS,
    openai: OPENAI_MODELS,
};
/** The two configurable slots (resolution order: primary first, then secondary). */
export const AI_PROVIDER_ROLES = ['primary', 'secondary'];
export const aiProviderRoleSchema = z.enum(AI_PROVIDER_ROLES);
// ---------------------------------------------------------------------------
// READ shape — one slot as returned to the frontend (key MASKED, never plaintext)
// ---------------------------------------------------------------------------
/**
 * One provider slot as READ by the admin UI. The key is NEVER returned: only a
 * masked tail + a boolean. There is no `model` on this shape — the effective
 * model is resolved per-prompt (`ai_prompts.primary_model`/`fallback_model`),
 * never per provider slot. `source` tells the UI whether this slot is
 * currently driven by a DB row or by the env fallback (so it can show
 * "из .env" vs "сохранено").
 */
export const aiProviderSlotSchema = z.object({
    /** Which slot this is ('primary' | 'secondary'). */
    role: aiProviderRoleSchema,
    /** Active provider for this slot. */
    provider: aiProviderSchema,
    /** true when a key is configured for this slot (DB ciphertext OR env fallback). */
    hasKey: z.boolean(),
    /** Masked tail of the configured key, e.g. '····1234'; null when hasKey=false. */
    maskedKey: z.string().nullable(),
    /** Where the effective config for this slot comes from right now. */
    source: z.enum(['db', 'env']),
});
/** GET /admin/ai-providers response — both slots, always present. */
export const aiProviderSettingsResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    primary: aiProviderSlotSchema,
    secondary: aiProviderSlotSchema,
});
// ---------------------------------------------------------------------------
// WRITE shape — PUT body (full replace of both slots; key optional per slot)
// ---------------------------------------------------------------------------
/**
 * One slot in the PUT body. `apiKey` is OPTIONAL and tri-state:
 *   - ABSENT/undefined → leave the stored key UNCHANGED (edit the provider only);
 *   - non-empty string → (re-)encrypt & store this new key;
 *   - empty string ''  → CLEAR the stored key (revert this slot to env fallback).
 *
 * There is no `model` here: the effective model is resolved per-prompt
 * (`ai_prompts.primary_model`/`fallback_model`, edited from the Model Matrix
 * table), never per provider slot. The masked read value must NEVER be
 * submitted as `apiKey` (it is not a real key); the UI sends `apiKey` only
 * when the admin typed a new one, otherwise omits it.
 *
 * `.strict()` rejects unknown keys so a forged `maskedKey`/`hasKey`/`updated_by`
 * cannot sneak through.
 */
export const aiProviderSlotUpdateSchema = z
    .object({
    provider: aiProviderSchema,
    /**
     * undefined ⇒ keep stored key; '' ⇒ clear key (env fallback); non-empty ⇒ set.
     * Max length guards against pathological input; real keys are well under this.
     */
    apiKey: z.string().max(500).optional(),
})
    .strict();
/** PUT /admin/ai-providers body — full replace of BOTH slots. */
export const aiProviderSettingsUpdateSchema = z
    .object({
    primary: aiProviderSlotUpdateSchema,
    secondary: aiProviderSlotUpdateSchema,
})
    .strict();
/**
 * PUT response — the freshly-saved settings, re-read (masked) so the UI refreshes
 * `hasKey`/`maskedKey`/`source` from authoritative state. Identical shape to GET.
 */
export const aiProviderSettingsUpdateResponseSchema = aiProviderSettingsResponseSchema;
//# sourceMappingURL=ai-provider-settings.js.map